import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage keys
const TOKEN_KEY = 'cardology_token';
const USER_KEY = 'cardology_user';
const TOKEN_EXPIRY_KEY = 'cardology_token_expiry';

// Helper to get stored data
const getStoredToken = () => {
  const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  
  // Check if token seems valid (basic format check)
  if (!token.includes('.')) return null;
  
  return token;
};

const getStoredUser = () => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    if (!userData) return null;
    const parsed = JSON.parse(userData);
    // Validate user object has required fields
    if (!parsed.id || !parsed.email) return null;
    return parsed;
  } catch {
    return null;
  }
};

const storeUserData = (userData, rememberMe = true) => {
  if (rememberMe && userData && userData.id && userData.email) {
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser());
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const [verificationCode, setVerificationCode] = useState(null);

  useEffect(() => {
    if (token) {
      // If we have stored user data, use it immediately while fetching fresh data
      const storedUser = getStoredUser();
      if (storedUser && !user) {
        setUser(storedUser);
      }
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      // Using apiClient with built-in retry logic
      const response = await apiClient.get('/auth/me');
      const userData = response.data;
      
      // Validate response has required fields
      if (!userData || !userData.id || !userData.email) {
        console.error('Invalid user data received from API');
        const storedUser = getStoredUser();
        if (storedUser) {
          setUser(storedUser);
        }
        return;
      }
      
      setUser(userData);
      // Update stored user data
      storeUserData(userData);
    } catch (error) {
      console.error('Error fetching user:', error?.response?.status, error?.message);
      
      // Only logout on explicit authentication errors (401, 403)
      // Don't logout on network errors, timeouts, or server errors
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Double-check it's a real auth error, not a temporary issue
        const storedUser = getStoredUser();
        if (storedUser && error.response.status === 401) {
          // Try one more time after a short delay before logging out
          await new Promise(resolve => setTimeout(resolve, 1000));
          try {
            const retryResponse = await apiClient.get('/auth/me');
            if (retryResponse.data?.id) {
              setUser(retryResponse.data);
              storeUserData(retryResponse.data);
              return;
            }
          } catch (retryError) {
            console.error('Retry failed, logging out');
          }
        }
        logout();
      } else {
        // Keep the stored user data on network/server errors
        const storedUser = getStoredUser();
        if (storedUser) {
          setUser(storedUser);
        }
        console.warn('Network error fetching user, using cached data');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = true) => {
    const response = await apiClient.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    
    // Clear both storages first
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    
    // Store token based on remember me preference
    if (rememberMe) {
      localStorage.setItem(TOKEN_KEY, newToken);
      storeUserData(userData, true);
    } else {
      sessionStorage.setItem(TOKEN_KEY, newToken);
    }
    
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, referralCode) => {
    const payload = { email, password, name };
    if (referralCode) payload.referral_code = referralCode;
    const response = await apiClient.post('/auth/register', payload);
    const { token: newToken, user: userData, verification_code } = response.data;
    
    // New registrations are remembered by default
    localStorage.setItem(TOKEN_KEY, newToken);
    storeUserData(userData, true);
    
    setToken(newToken);
    setUser(userData);
    
    // Store verification code to show to user
    if (verification_code) {
      setVerificationCode(verification_code);
    }
    return { user: userData, verificationCode: verification_code };
  };

  const verifyEmail = async (code) => {
    const response = await apiClient.post('/auth/verify-email', { code });
    if (response.data.verified) {
      const updatedUser = { ...user, email_verified: true };
      setUser(updatedUser);
      storeUserData(updatedUser);
      setVerificationCode(null);
    }
    return response.data;
  };

  const resendVerification = async () => {
    const response = await apiClient.post('/auth/resend-verification', {});
    if (response.data.verification_code) {
      setVerificationCode(response.data.verification_code);
    }
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    setVerificationCode(null);
  };

  // Refresh user data (useful after profile updates)
  const refreshUser = () => {
    if (token) {
      fetchUser();
    }
  };

  // Heartbeat - keep user marked as online
  useEffect(() => {
    if (!token) return;

    const sendHeartbeat = async () => {
      try {
        await apiClient.post('/social/heartbeat');
      } catch (error) {
        // Silently ignore heartbeat errors
        console.debug('Heartbeat failed:', error.message);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Send heartbeat every 2 minutes
    const interval = setInterval(sendHeartbeat, 2 * 60 * 1000);

    // Also send heartbeat on visibility change (tab focus)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      refreshUser,
      verificationCode,
      verifyEmail,
      resendVerification,
      clearVerificationCode: () => setVerificationCode(null)
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
